<template>
  <SchedulingLayout
    :isNextButtonDisabled="isNextButtonDisabled"
    :nextButtonLabel="getNextButtonLabel()"
    :showFooter="
      step === STEPS.SCHEDULE_SUCCESS || step === STEPS.CONTACT_HEALTH_PLAN
    "
    :hasBackButton="hasBackButton"
    @next="handleNext"
    @back="handleBack"
  >
    <template #default>
      <div class="flex flex-1 sm:my-50 justify-center">
        <div class="flex py-24 max-w-4xl flex-1 px-16">
          <QuestionSkeletonLoader v-if="isFetchingPcpsOnFile" class="w-full" />
          <SelectDoctorOnFile
            v-else-if="step === STEPS.PERSONAL_DOCTOR"
            :modelValue="steps.personalDoctor.selected"
            :options="steps.personalDoctor.options"
            @update:modelValue="steps.personalDoctor.selected = $event"
          />
          <div
            v-else-if="step === STEPS.CHOOSE_MY_DOCTOR"
            class="flex flex-col w-full gap-48"
          >
            <DoctorPreferenceSelect
              @open-zip-code-modal="openZipCodeModal"
              @close-zip-code-modal="closeZipCodeModal"
            />
            <QuestionWrapper
              :modelValue="steps.chooseMyDoctor.selected"
              :title="$t('health_actions.scheduling.choose_my_doctor_title')"
              :description="
                $t('health_actions.scheduling.choose_my_doctor_description')
              "
              :options="steps.chooseMyDoctor.options"
              @update:modelValue="steps.chooseMyDoctor.selected = $event"
            >
            </QuestionWrapper>
          </div>
          <DoctorSelect
            v-else-if="step === STEPS.USER_SELECTS_DOCTOR"
            @open-zip-code-modal="openZipCodeModal"
            @close-zip-code-modal="closeZipCodeModal"
            @open-doctor-preference-modal="openDoctorPreferenceModal"
          />
          <QuestionWrapper
            v-else-if="step === STEPS.ADD_OPTIONAL_DOCTORS"
            :modelValue="steps.addOptionalDoctors.selected"
            :title="$t('health_actions.scheduling.add_optional_doctors_title')"
            :options="steps.addOptionalDoctors.options"
            @update:modelValue="handleAddOptionalDoctorsChange"
          >
            <template #footer>
              <WarningBox
                v-if="
                  step === STEPS.ADD_OPTIONAL_DOCTORS &&
                    steps.addOptionalDoctors.selected === OPTIONS.NO
                "
                class="self-start w-full sm:w-1/2"
                textClass="text-sm"
                :text="
                  $t(
                    'health_actions.scheduling.add_optional_doctors_recommended'
                  )
                "
              />
              <AddOptionalDoctors
                v-if="
                  step === STEPS.ADD_OPTIONAL_DOCTORS &&
                    steps.addOptionalDoctors.selected === OPTIONS.YES
                "
                @open-zip-code-modal="openZipCodeModal"
                @close-zip-code-modal="closeZipCodeModal"
              />
            </template>
          </QuestionWrapper>

          <QuestionWrapper
            v-else-if="step === STEPS.SELECT_AVAILABILITY"
            :modelValue="steps.selectAvailability.selected"
            :title="$t('health_actions.scheduling.select_availability_title')"
            :description="
              $t('health_actions.scheduling.select_availability_description')
            "
            :options="steps.selectAvailability.options"
            @update:modelValue="handleSelectAvailabilityChange"
          >
            <template #footer>
              <AvailabilitySelect
                v-if="steps.selectAvailability.selected === OPTIONS.NO"
                class="mt-24"
              />
            </template>
          </QuestionWrapper>
          <ApproveScheduling
            v-else-if="step === STEPS.APPROVE_SCHEDULING"
            @edit-doctor="returnToChooseMyDoctor"
            @edit-availability="returnToAvailability"
            @edit-preferences="openDoctorPreferenceModal"
            @open-zip-code-modal="openZipCodeModal"
            @close-zip-code-modal="closeZipCodeModal"
            @open-doctor-preference-modal="openDoctorPreferenceModal"
            @close-doctor-preference-modal="closeDoctorPreferenceModal"
          />
          <ScheduleSuccess
            v-else-if="step === STEPS.SCHEDULE_SUCCESS"
            :phoneNumber="primaryPhoneNumber"
          />
          <ContactHealthPlan v-else-if="step === STEPS.CONTACT_HEALTH_PLAN" />
        </div>
      </div>
      <ChangeZipCodeModal
        v-if="showZipCodeModal"
        @close-zip-code-modal="closeZipCodeModal"
      />
      <DoctorPreferenceModal
        v-if="showDoctorPreferenceModal"
        @close-doctor-preference-modal="closeDoctorPreferenceModal"
      />
    </template>
  </SchedulingLayout>
</template>

<script>
import SchedulingLayout from '@components/layouts/SchedulingLayout'
import AvailabilitySelect from './AvailabilitySelect.vue'
import QuestionWrapper from './QuestionWrapper.vue'
import ApproveScheduling from './ApproveScheduling.vue'
import ScheduleSuccess from './ScheduleSuccess.vue'
import { mapGetters, mapActions } from 'vuex'
import DoctorSelect from './DoctorSelect.vue'
import WarningBox from './WarningBox.vue'
import AddOptionalDoctors from './AddOptionalDoctors.vue'
import DoctorPreferenceSelect from './DoctorPreferenceSelect.vue'
import ContactHealthPlan from './ContactHealthPlan.vue'
import SelectDoctorOnFile from './SelectDoctorOnFile.vue'
import ChangeZipCodeModal from './ChangeZipCodeModal.vue'
import DoctorPreferenceModal from './DoctorPreferenceModal.vue'
import QuestionSkeletonLoader from './QuestionSkeletonLoader.vue'

const STEPS = {
  PERSONAL_DOCTOR: 'personal_doctor',
  CHOOSE_MY_DOCTOR: 'choose_my_doctor',
  USER_SELECTS_DOCTOR: 'user_selects_doctor',
  ADD_OPTIONAL_DOCTORS: 'add_optional_doctors',
  SELECT_AVAILABILITY: 'select_availability',
  APPROVE_SCHEDULING: 'approve_scheduling',
  SCHEDULE_SUCCESS: 'schedule_success',
  CONTACT_HEALTH_PLAN: 'contact_your_health_plan',
}

const OPTIONS = {
  YES: 'yes',
  NO: 'no',
}

export default {
  components: {
    SchedulingLayout,
    SelectDoctorOnFile,
    DoctorSelect,
    AvailabilitySelect,
    QuestionWrapper,
    ApproveScheduling,
    ScheduleSuccess,
    WarningBox,
    AddOptionalDoctors,
    DoctorPreferenceSelect,
    ContactHealthPlan,
    ChangeZipCodeModal,
    DoctorPreferenceModal,
    QuestionSkeletonLoader,
  },
  data() {
    return {
      step: null,
      STEPS, // Expose constants to template
      OPTIONS, // Expose constants to template
      showZipCodeModal: false,
      showDoctorPreferenceModal: false,
      steps: {
        personalDoctor: {
          name: STEPS.PERSONAL_DOCTOR,
          options: [
            {
              code: OPTIONS.YES,
              title: 'health_actions.scheduling.personal_doctor_yes',
            },
            {
              code: OPTIONS.NO,
              title: 'health_actions.scheduling.personal_doctor_no',
            },
          ],
          selected: null,
        },
        chooseMyDoctor: {
          name: STEPS.CHOOSE_MY_DOCTOR,
          options: [
            {
              code: OPTIONS.YES,
              title: 'health_actions.scheduling.choose_my_doctor_yes',
            },
            {
              code: OPTIONS.NO,
              title: 'health_actions.scheduling.choose_my_doctor_no',
            },
          ],
          selected: null,
        },
        selectAvailability: {
          name: STEPS.SELECT_AVAILABILITY,
          options: [
            {
              code: OPTIONS.YES,
              title: 'health_actions.scheduling.any_availability_yes',
            },
            {
              code: OPTIONS.NO,
              title: 'health_actions.scheduling.any_availability_no',
            },
          ],
          selected: null,
        },
        addOptionalDoctors: {
          name: STEPS.ADD_OPTIONAL_DOCTORS,
          options: [
            {
              code: OPTIONS.YES,
              title: 'common.yes',
            },
            {
              code: OPTIONS.NO,
              title: 'common.no',
            },
          ],
          selected: null,
        },
      },
      navigationMap: {
        [STEPS.PERSONAL_DOCTOR]: (vm) => {
          if (vm.steps.personalDoctor.selected === OPTIONS.YES) {
            vm.updateSelectedDoctors({ doctor: vm.pcpsOnFile[0] })
            return STEPS.SELECT_AVAILABILITY
          } else if (
            vm.pcpsOnFile.find(
              (doctor) => doctor.name === vm.steps.personalDoctor.selected
            )
          ) {
            // DoctorCardSelect handles setting the selected doctor internally,
            // so we just do the navigation to the next screen here
            return STEPS.SELECT_AVAILABILITY
          } else {
            return vm.healthPlanRequiresPersonalDoctor
              ? STEPS.CONTACT_HEALTH_PLAN
              : STEPS.CHOOSE_MY_DOCTOR
          }
        },
        [STEPS.CHOOSE_MY_DOCTOR]: (vm) => {
          if (vm.steps.chooseMyDoctor.selected === OPTIONS.YES) {
            return STEPS.SELECT_AVAILABILITY
          } else {
            return STEPS.USER_SELECTS_DOCTOR
          }
        },
        [STEPS.USER_SELECTS_DOCTOR]: () => STEPS.ADD_OPTIONAL_DOCTORS,
        [STEPS.ADD_OPTIONAL_DOCTORS]: () => STEPS.SELECT_AVAILABILITY,
        [STEPS.SELECT_AVAILABILITY]: () => STEPS.APPROVE_SCHEDULING,
        [STEPS.APPROVE_SCHEDULING]: (vm) => {
          vm.scheduleAppointment()
          return STEPS.SCHEDULE_SUCCESS
        },
      },
      navigationStack: [],
    }
  },
  computed: {
    ...mapGetters('scheduling', [
      'pcpsOnFile',
      'healthPlanRequiresPersonalDoctor',
      'selectedDoctors',
      'selectedAvailability',
      'userHasSelectedAvailability',
      'getDoctorPreferences',
      'zipCode',
      'isFetchingPcpsOnFile',
    ]),
    ...mapGetters('member', {
      zipCodeOnFile: 'zipCode',
      primaryPhoneNumber: 'primaryPhoneNumber',
    }),
    hasPcpsOnFile() {
      return this.pcpsOnFile?.length > 0
    },
    isNextButtonDisabled() {
      const validationRules = {
        [STEPS.PERSONAL_DOCTOR]: () => !this.steps.personalDoctor.selected,
        [STEPS.CHOOSE_MY_DOCTOR]: () => {
          if (this.steps.chooseMyDoctor.selected === null) {
            return true
          }

          return (
            !this.getDoctorPreferences.distance ||
            !this.getDoctorPreferences.gender
          )
        },
        [STEPS.USER_SELECTS_DOCTOR]: () => this.selectedDoctors.length === 0,
        [STEPS.ADD_OPTIONAL_DOCTORS]: () => false, // Always enabled
        [STEPS.SELECT_AVAILABILITY]: () =>
          !this.steps.selectAvailability.selected ||
          (!this.userHasSelectedAvailability &&
            this.steps.selectAvailability.selected === OPTIONS.NO),
        [STEPS.APPROVE_SCHEDULING]: () => false, // Always enabled
      }

      return validationRules[this.step] ? validationRules[this.step]() : true
    },
    hasBackButton() {
      const steps = [
        STEPS.SELECT_AVAILABILITY,
        STEPS.USER_SELECTS_DOCTOR,
        STEPS.ADD_OPTIONAL_DOCTORS,
        STEPS.APPROVE_SCHEDULING,
      ]

      //add CHOOSE_MY_DOCTOR to list if there are pcps on file
      const stepsWithBackButton = this.hasPcpsOnFile
        ? [STEPS.CHOOSE_MY_DOCTOR, ...steps]
        : steps

      return stepsWithBackButton.includes(this.step)
    },
    isDoctorPreferencesSelected() {
      return (
        this.getDoctorPreferences.distance && this.getDoctorPreferences.gender
      )
    },
  },
  watch: {
    getDoctorPreferences: function() {
      if (this.step === STEPS.CHOOSE_MY_DOCTOR) {
        this.fetchProvidersWithPreferences()
      }
    },
  },
  async mounted() {
    // if we haven't fetched the pcps on file yet, do so now
    if (this.pcpsOnFile === undefined) {
      await this.fetchPcpsOnFile()
    }
    if (this.hasPcpsOnFile) {
      this.step = STEPS.PERSONAL_DOCTOR
    } else {
      this.step = this.healthPlanRequiresPersonalDoctor
        ? STEPS.CONTACT_HEALTH_PLAN
        : STEPS.CHOOSE_MY_DOCTOR
    }
    this.setZipCode(this.zipCodeOnFile)
  },
  methods: {
    ...mapActions('scheduling', [
      'resetAvailability',
      'resetDoctor',
      'updateSelectedDoctors',
      'keepOnlyFirstDoctor',
      'fetchProviders',
      'setZipCode',
      'setShouldAutoFetchProviders',
      'scheduleAppointment',
      'fetchPcpsOnFile',
    ]),
    /**
     * Handles change in the add optional doctors selection
     */
    handleAddOptionalDoctorsChange(value) {
      this.steps.addOptionalDoctors.selected = value
      if (value === OPTIONS.NO) {
        this.keepOnlyFirstDoctor()
      }
    },
    /**
     * Handles change in the any availability selection
     */
    handleSelectAvailabilityChange(value) {
      this.steps.selectAvailability.selected = value
      if (value === OPTIONS.YES) {
        this.resetAvailability()
      }
    },
    /**
     * Returns to availability selection screen and resets availability data
     */
    returnToAvailability() {
      this.resetAvailability()
      this.step = STEPS.SELECT_AVAILABILITY
    },
    /**
     * Returns to doctor selection screen and resets doctor data
     */
    returnToChooseMyDoctor() {
      this.resetDoctor()
      this.step = STEPS.CHOOSE_MY_DOCTOR
    },
    /**
     * Handles navigation to the next step based on current selections
     */
    handleNext() {
      const nextStepFn = this.navigationMap[this.step]
      if (nextStepFn) {
        this.navigationStack.push(this.step)
        this.step = nextStepFn(this)
      }
    },
    /**
     * Handles navigation to the previous step
     */
    handleBack() {
      this.step = this.navigationStack.pop()

      // Reset data when going back to certain steps
      if (this.step === STEPS.CHOOSE_MY_DOCTOR) {
        this.resetDoctor()
      } else if (this.step === STEPS.SELECT_AVAILABILITY) {
        this.resetAvailability()
      }
    },
    /**
     * Returns the appropriate next button label based on the current step
     */
    getNextButtonLabel() {
      return this.step === STEPS.APPROVE_SCHEDULING
        ? this.$t('health_actions.scheduling.approve_scheduling')
        : this.$t('common.next_btn')
    },
    /**
     * Fetches providers based on current doctor preferences
     */
    fetchProvidersWithPreferences() {
      if (this.isDoctorPreferencesSelected) {
        this.fetchProviders({
          distance: this.getDoctorPreferences.distance,
          providerGender: this.getDoctorPreferences.gender,
          address: this.zipCode,
        })
      }
    },
    openZipCodeModal() {
      this.showZipCodeModal = true
    },
    closeZipCodeModal() {
      this.showZipCodeModal = false
    },
    openDoctorPreferenceModal() {
      this.showDoctorPreferenceModal = true
    },
    closeDoctorPreferenceModal() {
      this.showDoctorPreferenceModal = false
    },
  },
}
</script>
